import { memo, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star } from 'lucide-react';
import { Offer } from '../lib/supabase';
import { Badge } from './Badge';
import { optimizeImageUrl } from '../lib/imageUtils';

interface OfferCardProps {
  offer: Offer;
  rating?: { average: number; count: number };
  currencyRates?: { [key: string]: number };
  priority?: boolean;
  searchParams?: {
    checkIn?: string;
    checkOut?: string;
    guests?: number;
  };
}

export const OfferCard = memo(function OfferCard({ offer, rating, currencyRates, priority, searchParams }: OfferCardProps) {
  const navigate = useNavigate();

  const convertedPrices = useMemo(() => {
    if (!currencyRates) return { eur: null, usd: null };
    const eurRate = currencyRates['EUR'];
    const usdRate = currencyRates['USD'];
    return {
      eur: eurRate ? Math.round((offer.price / eurRate) * 100) / 100 : null,
      usd: usdRate ? Math.round((offer.price / usdRate) * 100) / 100 : null,
    };
  }, [offer.price, currencyRates]);

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition transform hover:-translate-y-1">
      <div className="h-64 bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center relative">
        {offer.images && offer.images.length > 0 ? (
          <img
            src={optimizeImageUrl(offer.images[0], 480)}
            alt={offer.title}
            className="w-full h-full object-cover"
            loading={priority ? 'eager' : 'lazy'}
            decoding={priority ? 'sync' : 'async'}
            fetchPriority={priority ? 'high' : undefined}
            width={480}
            height={256}
          />
        ) : (
          <Star className="text-white" size={48} />
        )}
        {offer.badges && offer.badges.length > 0 && (
          <div className="absolute top-2 left-2 right-2 flex flex-wrap gap-1.5">
            {offer.badges.slice(0, 2).map((badge) => (
              <Badge key={badge} type={badge} size="sm" />
            ))}
          </div>
        )}
      </div>
      <div className="p-4">
        {offer.badges && offer.badges.length > 2 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {offer.badges.slice(2).map((badge) => (
              <Badge key={badge} type={badge} size="sm" />
            ))}
          </div>
        )}
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-lg font-bold text-gray-800 flex-1 line-clamp-1">{offer.title}</h3>
          {rating && (
            <div className="flex items-center ml-2">
              <Star size={14} className="text-yellow-500 fill-yellow-500" />
              <span className="ml-1 text-sm font-semibold text-gray-800">{rating.average}</span>
              <span className="ml-1 text-xs text-gray-500">({rating.count})</span>
            </div>
          )}
        </div>
        <p className="text-gray-600 text-sm mb-3 line-clamp-1">{offer.description}</p>
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-xl font-bold text-blue-600">
              {offer.price.toLocaleString()} {offer.currency}
            </span>
            <div className="flex gap-2 text-xs text-gray-500 mt-1">
              {convertedPrices.eur !== null && (
                <span>{convertedPrices.eur} &euro;</span>
              )}
              {convertedPrices.usd !== null && (
                <span>{convertedPrices.usd} $</span>
              )}
            </div>
          </div>
          <button
            onClick={() => navigate(`/offer/${offer.id}`, {
              state: searchParams
            })}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm"
          >
            Voir détails
          </button>
        </div>
      </div>
    </div>
  );
});
